// A picture of the page for a person's in-app report, rendered in their own
// browser and masked before anything leaves it (ADR-0518 decision 5a).
//
// **How it is drawn, and why that is safe.** The page is cloned. Every
// element of the clone takes the computed style of its original, and the clone
// is then masked (below). It is serialized as XHTML inside an SVG
// `foreignObject` and drawn onto a canvas. A browser loads nothing for an SVG
// drawn as an image (no stylesheet, font, image or script), so the rendering
// fetches nothing, from this origin or any other, which is decision 5a's
// *"fetches nothing from a third party"* held by the browser rather than by a
// list. The picture is the viewport the person is looking at.
//
// **What is masked, and it fails closed.** In the clone, before it is
// serialized:
// - every element marked `data-report-mask` (a revealed key or passphrase, a
//   token shown once, the device console, a map) becomes a solid box of its
//   size with no text and no children;
// - **every form control** (input, textarea, select) becomes a box, whatever the
//   field, so a key being typed into a form is never in the picture. Its label
//   is an element of its own and is drawn;
// - every image, canvas, video and frame becomes a labelled box, so nothing
//   drawn by script or by a third party is carried; an image of this origin is
//   drawn as itself only if it has loaded and can be read back;
// - the report panel itself (`data-report-exclude`) is removed.
// Then, after the picture is drawn, every marked element's and form control's
// box is painted over again on the canvas, from the live page's own geometry.
// A mask the clone missed is still covered.
//
// `maskedClone` is the first half alone, so a test can read what the picture
// would carry without a browser that can draw.

const MASK = "[data-report-mask], input, textarea, select";
const MEDIA = "img, canvas, video, iframe, object, embed, picture";
const BOX = "#c9cdd2";
const BOX_TEXT = "#5b6168";

/** Elements of `root`'s subtree in document order, `root` first. */
function elements(root: Element): Element[] {
  return [root, ...Array.from(root.querySelectorAll("*"))];
}

function box(doc: Document, original: Element, label?: string): HTMLElement {
  const r = original.getBoundingClientRect();
  const cs = original.ownerDocument.defaultView?.getComputedStyle(original);
  const div = doc.createElement("div");
  div.setAttribute("data-report-masked", "");
  div.style.cssText = [
    `display:${cs?.display === "inline" ? "inline-block" : cs?.display || "block"}`,
    `width:${Math.round(r.width)}px`, `height:${Math.round(r.height)}px`,
    `margin:${cs?.margin ?? "0"}`, `box-sizing:border-box`, `background:${BOX}`, `border-radius:2px`,
    `color:${BOX_TEXT}`, `font:12px system-ui,sans-serif`, `overflow:hidden`, `vertical-align:middle`,
  ].join(";");
  if (label) div.textContent = label;
  return div;
}

/** An image of this origin that has loaded, as a data URL, or undefined. */
function imageData(img: HTMLImageElement): string | undefined {
  try {
    if (!img.complete || img.naturalWidth === 0) return undefined;
    if (new URL(img.currentSrc || img.src, location.href).origin !== location.origin) return undefined;
    const c = document.createElement("canvas");
    c.width = img.naturalWidth;
    c.height = img.naturalHeight;
    c.getContext("2d")?.drawImage(img, 0, 0);
    return c.toDataURL("image/png");
  } catch {
    return undefined;
  }
}

/** The page's clone with its styles inlined and everything above masked.
 * `copyStyles` is off in a test, whose document has no layout. */
export function maskedClone(doc: Document = document, copyStyles = true): HTMLElement {
  const source = doc.body;
  const clone = source.cloneNode(true) as HTMLElement;
  const originals = elements(source);
  const copies = elements(clone);
  const view = doc.defaultView;
  if (copyStyles && view) {
    originals.forEach((o, i) => {
      const c = copies[i] as HTMLElement | undefined;
      if (!c?.style) return;
      const cs = view.getComputedStyle(o);
      for (let k = 0; k < cs.length; k++) {
        const name = cs.item(k);
        c.style.setProperty(name, cs.getPropertyValue(name), cs.getPropertyPriority(name));
      }
      // A background image is a URL the picture would not load anyway; drop it
      // so the serialized markup names none.
      c.style.removeProperty("background-image");
    });
  }
  // Replace from the deepest out, so a box never holds a replaced child.
  const replace: [Element, HTMLElement][] = [];
  const drop: Element[] = [];
  originals.forEach((o, i) => {
    const c = copies[i];
    if (!c) return;
    if (o.closest("[data-report-exclude]")) {
      if (o.matches("[data-report-exclude]")) drop.push(c);
      return;
    }
    if (o.closest("[data-report-mask]") && !o.matches("[data-report-mask]")) return;
    if (o.matches(MASK)) replace.push([c, box(doc, o)]);
    else if (o.matches(MEDIA)) {
      const data = o instanceof HTMLImageElement ? imageData(o) : undefined;
      if (data) {
        (c as HTMLImageElement).src = data;
        c.removeAttribute("srcset");
      } else replace.push([c, box(doc, o, o.matches("img, picture") ? "image" : "")]);
    }
  });
  for (const c of drop) c.remove();
  for (const [c, b] of replace) if (c.isConnected || clone.contains(c)) c.replaceWith(b);
  for (const s of Array.from(clone.querySelectorAll("script, noscript, style, link"))) s.remove();
  // An attribute can carry a value too: a placeholder, a title naming a key.
  for (const el of Array.from(clone.querySelectorAll("[value]"))) el.removeAttribute("value");
  return clone;
}

/** The live page's boxes that must be covered, in viewport coordinates. */
function coverRects(doc: Document): DOMRect[] {
  return Array.from(doc.querySelectorAll(MASK))
    .filter((el) => !el.closest("[data-report-exclude]"))
    .map((el) => el.getBoundingClientRect())
    .filter((r) => r.width > 0 && r.height > 0);
}

/** The viewport as a masked PNG. Rejects where the browser cannot draw it;
 * the panel then says so and the report is sent without one. */
export async function renderScreenshot(doc: Document = document): Promise<Blob> {
  const view = doc.defaultView ?? window;
  const width = view.innerWidth;
  const height = view.innerHeight;
  const dpr = Math.min(view.devicePixelRatio || 1, 2);
  const clone = maskedClone(doc);
  clone.style.setProperty("margin", "0");
  const xhtml = new XMLSerializer().serializeToString(clone);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">` +
    `<foreignObject x="${-view.scrollX}" y="${-view.scrollY}" width="${doc.documentElement.scrollWidth}" height="${doc.documentElement.scrollHeight}">${xhtml}</foreignObject></svg>`;
  const img = new Image();
  img.decoding = "sync";
  const loaded = new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error("the page could not be drawn as a picture"));
  });
  img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
  await loaded;
  const canvas = doc.createElement("canvas");
  canvas.width = Math.round(width * dpr);
  canvas.height = Math.round(height * dpr);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("this browser offers no canvas to draw on");
  ctx.scale(dpr, dpr);
  ctx.fillStyle = view.getComputedStyle(doc.body).backgroundColor || "#ffffff";
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);
  // The second layer: whatever the clone drew, the live page's masked boxes
  // are painted over.
  ctx.fillStyle = BOX;
  for (const r of coverRects(doc)) ctx.fillRect(r.left, r.top, r.width, r.height);
  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("the picture could not be encoded"))), "image/png"));
}
